import { US_STATE_NAMES } from './regions.js';
// ============================================================================
// Shared Reach / Target / Safety CHIP vocabulary — the one place the band chip
// colors live.
//
// Extracted verbatim from SimilarSchoolsPanel (#1188) so surfaces outside the
// Launchpad — the Concierge chat's in-thread school picker — speak the same
// visual language instead of forking a fourth copy of the same four classes.
// (listBalance.js owns a different thing: the solid hex colors of the balance
// RING. Chips and ring are separate marks and deliberately stay separate.)
// ============================================================================

export const BAND_CHIP = {
    Safety: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Likely: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Target: 'bg-blue-50 text-blue-700 border-blue-200',
    Reach: 'bg-amber-50 text-amber-700 border-amber-200',
    'Super-Reach': 'bg-rose-50 text-rose-600 border-rose-200',
};

// For a band that exists but isn't one we have a color for. A caller with NO
// band at all should render no chip rather than reach for this.
export const BAND_CHIP_UNKNOWN = 'bg-gray-50 text-gray-600 border-gray-200';

/**
 * Chip classes for a band label, or null when the band is absent/unrecognised.
 * Tolerant of the two casings already in the codebase (`Safety` from the
 * fit-candidates API, `SUPER_REACH` from stored fit categories) so neither
 * caller has to normalize before asking.
 */
export function bandChipClass(band) {
    if (typeof band !== 'string') return null;
    const raw = band.trim();
    if (!raw) return null;
    if (BAND_CHIP[raw]) return BAND_CHIP[raw];
    const titled = raw.toLowerCase().split(/[\s_-]+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join('-');
    return BAND_CHIP[titled] || null;
}

/**
 * An admit rate as a whole-number percent, or null when there is no usable
 * value. NEVER returns 0 for a missing rate — a school with no published rate
 * and a school that admits nobody must not read the same.
 *
 * The wire contract is a FRACTION (0.67 → 67). Values above 1 are treated as
 * an already-converted percent rather than printed as "6700%"; anything above
 * 100, negative, or non-finite is not a rate at all and reads as unknown.
 * A literal 1 resolves to 100% — it is a fraction by contract.
 */
export function admitRatePercent(raw) {
    const n = typeof raw === 'number' ? raw : NaN;
    if (!Number.isFinite(n) || n < 0 || n > 100) return null;
    return Math.round(n <= 1 ? n * 100 : n);
}


// ============================================================================
// The band PANEL/HEADER palette — Launchpad verdict panel + Fit Analysis
// header pill (founder, 2026-08-08: the header showed SAFETY blue / TARGET
// green, swapped against the Launchpad, because each surface carried its own
// copy). Same semantics as BAND_CHIP above (Safety green, Target blue, Reach
// amber, Super Reach red) at panel-strength contrast — chips stay the soft
// -50 tints, panels get the saturated pair. The band always carries its word.
// ============================================================================

export const FIT_BANDS = {
    SAFETY: {
        bg: '#DCEFE0', fg: '#1A4D2E', label: 'Safety', icon: '🛡️',
        chip: 'bg-[#DCEFE0] text-[#1A4D2E] border-[#B9DCC2]',
    },
    TARGET: {
        bg: '#DCE9F5', fg: '#1B4F72', label: 'Target', icon: '🎯',
        chip: 'bg-[#DCE9F5] text-[#1B4F72] border-[#B9D2E8]',
    },
    REACH: {
        bg: '#FCE9DC', fg: '#B45309', label: 'Reach', icon: '🔼',
        chip: 'bg-[#FCE9DC] text-[#B45309] border-[#F0CBAA]',
    },
    SUPER_REACH: {
        bg: '#FCE4DC', fg: '#C0392B', label: 'Super Reach', icon: '🚀',
        chip: 'bg-[#FCE4DC] text-[#C0392B] border-[#EFBCAC]',
    },
};

// ============================================================================
// The UNSCORED state — a school on the list whose fit has not arrived (still
// computing, or the add failed). Founder, 2026-10-09 (#3607): five screens
// defaulted a missing fit_category to 'TARGET' and one painted it green, so a
// student could watch Harvard read as a green "Target". A missing band is
// absence, never a guess: it reads "Not scored yet", in grey, and never
// counts as Target in any sort, group, filter or count.
// __tests__/unscoredFitGuard.test.js fails if `|| 'TARGET'` / `?? 'TARGET'`
// comes back anywhere under frontend/src.
// ============================================================================

export const UNSCORED = 'UNSCORED';
export const UNSCORED_LABEL = 'Not scored yet';
export const UNSCORED_BAND = {
    bg: '#F1F0EC', fg: '#4B4A45', label: UNSCORED_LABEL, icon: '',
    chip: 'bg-gray-50 text-gray-600 border-gray-200',
};

const KNOWN_BANDS = new Set(Object.keys(FIT_BANDS));

/**
 * The first real band among the candidates, normalized to the stored
 * spelling ('Super Reach' / 'super_reach' -> 'SUPER_REACH'), or UNSCORED.
 * Pass the candidates in trust order — the student's own fit first, then any
 * legitimate fallback (soft_fit_category). An unrecognised value ('UNKNOWN',
 * the KB's no-rate band) is skipped, never promoted to a band.
 */
export function fitBandOrUnscored(...candidates) {
    for (const c of candidates) {
        if (typeof c !== 'string') continue;
        const key = c.trim().toUpperCase().replace(/[\s-]+/g, '_');
        if (KNOWN_BANDS.has(key)) return key;
    }
    return UNSCORED;
}

export const isUnscored = (category) => fitBandOrUnscored(category) === UNSCORED;

export const bandFor = (category) => FIT_BANDS[category]
    || (category === UNSCORED ? UNSCORED_BAND : null);


// ============================================================================
// The SCHOOL DOOR line — which admit rate banded a fit, in the student's words.
//
// The fit engine (profile_manager_v2/fit_doors.py) stamps every fit with
// acceptance_rate_used {value, basis, note?}: basis is in_state |
// out_of_state | international | overall, and note says when the overall rate
// stood in for the student's residency (#3046). This is the ONE renderer of
// that stamp — the Launchpad card and the Explorer verdict panel both read it,
// and tests/fixtures/residency_door_cases.json pins it to the engine's output.
// ============================================================================

const DOOR_WHO = {
    out_of_state: 'out-of-state applicants',
    international: 'international applicants',
};

/**
 * An admit rate as the card shows it: one decimal, never rounded twice
 * (#3622). The KB stores rates at two decimals, so Yale's 2,387 / 50,264 =
 * 4.749% is stored as 4.75, and rounding THAT to one decimal said 4.8%, a
 * tenth high. A two-decimal value whose last digit is 5 is a tie only the
 * counts could break (the true rate is anywhere in 4.745-4.755), so it is
 * shown as stored, "4.75%", never a guessed digit. Missing is missing:
 * Number(null) is 0, and an unpublished rate must never render as "0%".
 */
export function fmtRatePct(v) {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    const hundredths = Math.round(n * 100);
    const atTwoDecimals = Math.abs(n * 100 - hundredths) < 1e-6;
    if (atTwoDecimals && Math.abs(hundredths) % 10 === 5) return `${(hundredths / 100).toFixed(2)}%`;
    return `${Math.round(n * 10) / 10}%`;
}

const fmtDoorRate = fmtRatePct;

/**
 * "35.9% for Texas residents (overall 22.2%)", "7.5% for out-of-state
 * applicants (overall 22.2%)", "22.2% overall; this school's out-of-state rate
 * isn't on file", or null when there is no rate at all. No dashes: the line is
 * a student-facing sentence and human-copy bans the em dash (#4426, the
 * founder flagged "96.45% — California residents" on Sofia's ad card).
 */
export function schoolDoorText(rateUsed, overallRate, stateName) {
    const basis = rateUsed?.basis;
    const used = fmtDoorRate(rateUsed?.value);
    const overall = fmtDoorRate(overallRate);
    const differs = overall && used
        && Math.round(Number(overallRate)) !== Math.round(Number(rateUsed.value));
    if (used && (basis === 'in_state' || DOOR_WHO[basis])) {
        // A location's two-letter code reads as the state's name ("CA" ->
        // "California"), so two cards side by side say it one way (#4421:
        // UCLA's card said "CA residents" beside Merced's "California").
        const state = stateName ? (US_STATE_NAMES[String(stateName).toUpperCase()] || stateName) : null;
        const who = basis === 'in_state'
            ? (state ? `${state} residents` : 'in-state applicants') : DOOR_WHO[basis];
        return `${used} for ${who}${differs ? ` (overall ${overall})` : ''}`;
    }
    const rate = used || overall;
    if (!rate) return null;
    const note = typeof rateUsed?.note === 'string' ? rateUsed.note : '';
    // The note's own words after "Based on the overall rate; ": the line
    // already says "overall".
    const why = note.replace(/^based on the overall rate;\s*/i, '').replace(/\.$/, '');
    return why ? `${rate} overall; ${why}` : `${rate} overall`;
}

/**
 * The MAJOR DOOR line from the fit's major_banding_used stamp: "7.3% for
 * Computer Science", "competitive; Computer Science admits separately" when
 * the major has no published rate (absence is absence, never 0%), or null
 * when the fit used no major door. One renderer for the Launchpad card and
 * the Explorer panel, which each held their own dashed copy before #4426.
 */
export function majorDoorText(majorUsed) {
    if (!majorUsed?.major) return null;
    const rate = fmtRatePct(majorUsed.major_rate);
    return rate ? `${rate} for ${majorUsed.major}`
        : `competitive; ${majorUsed.major} admits separately`;
}
